import type { CustomRequirementCondition, DataIdentifier } from '@onefootprint/request-types/dashboard';
import type { Question } from '../types';

type Prompt = Exclude<Question, { kind: 'text_block' }>;

const mapFields = (
  condition: CustomRequirementCondition,
  map: (field: string) => string,
): CustomRequirementCondition =>
  condition.kind === 'expression'
    ? { ...condition, field: map(condition.field) as DataIdentifier }
    : { ...condition, conditions: condition.conditions.map(c => mapFields(c, map)) };

// A draft can save two questions with one data identifier (only publishing rejects it), so a condition
// resolves as the backend does: to the first match before it, never to a question after it.
const remap = (questions: Question[], from: (q: Prompt) => string, to: (q: Prompt) => string): Question[] =>
  questions.map((q, index) => {
    if (!q.condition) return q;
    const earlier = questions.slice(0, index).filter((p): p is Prompt => p.kind !== 'text_block');
    const resolve = (field: string) => {
      const target = earlier.find(p => from(p) === field);
      return target ? to(target) : field;
    };
    return { ...q, condition: mapFields(q.condition, resolve) };
  });

// Inside the form a condition points at its target's `id`, not its data identifier, so it can target a
// question that has none yet and keeps pointing at it when the identifier changes.
export const conditionsToQuestionIds = (questions: Question[]) =>
  remap(
    questions,
    q => q.dataIdentifier,
    q => q.id,
  );

export const conditionsToDataIdentifiers = (questions: Question[]) =>
  remap(
    questions,
    q => q.id,
    q => q.dataIdentifier,
  );
